import { useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trophy, ArrowSquareOut, Check, Crown } from "@phosphor-icons/react";
import { buildCheckoutUrl } from "@/lib/checkoutUrl";
import PaymentTrust from "@/components/PaymentTrust";

const ACCENT = {
  Silver:   "from-slate-100 to-white text-slate-700 ring-slate-300",
  Gold:     "from-amber-50 to-white text-amber-800 ring-amber-400",
  Platinum: "from-indigo-50 to-white text-indigo-800 ring-indigo-400",
};

export default function BuyUpgrades() {
  const { siteSettings, user } = useAuth();
  const packs = useMemo(
    () => (Array.isArray(siteSettings?.upgrade_packs) ? siteSettings.upgrade_packs : []),
    [siteSettings]
  );

  const buy = (pack) => {
    const url = buildCheckoutUrl(pack.url, user, {
      pack_id: pack.id,
      plan: pack.name,
    });
    if (!url) {
      toast.info(`${pack.name} upgrade isn't live yet`, {
        description: "The admin hasn't set the checkout link for this pack.",
      });
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400">Store</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            Buy Upgrades
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            Boost your surf multiplier, priority queue and badges. Current plan:{" "}
            <span className="font-black text-slate-800 dark:text-slate-100">{user?.membership || "Free"}</span>.
          </p>
        </div>

        {packs.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center text-slate-500 dark:text-slate-400">
            Upgrade packs are being prepared. Please check back shortly.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {packs.map((p, i) => {
            const accent = ACCENT[p.name] || "from-white to-white text-slate-700 ring-slate-300";
            const features = Array.isArray(p.features) ? p.features : [];
            const highlight = (p.name || "").toLowerCase() === "gold";
            return (
              <div
                key={p.id || `pack-${i}`}
                data-testid={`upgrade-pack-${p.id || i}`}
                className={`relative rounded-3xl bg-gradient-to-br ${accent} ring-2 p-6 flex flex-col transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl dark:from-slate-900 dark:to-slate-900 dark:text-slate-100`}
              >
                {highlight && (
                  <span className="absolute -top-3 right-4 inline-flex items-center gap-1 rounded-full bg-amber-400 text-amber-950 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 shadow">
                    <Crown size={10} weight="fill" /> Most popular
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <Trophy size={22} weight="fill" />
                  <span className="text-xs font-black uppercase tracking-widest">{p.name}</span>
                </div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                    ${Number(p.price_usd).toFixed(2)}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">/ {p.period || "month"}</span>
                </div>
                <ul className="mt-5 space-y-2 flex-1">
                  {features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                      <Check size={16} weight="bold" className="text-lime-600 dark:text-lime-400 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`upgrade-buy-${p.id || i}`}
                  onClick={() => buy(p)}
                  className={`mt-6 inline-flex items-center justify-center gap-1 rounded-full px-4 py-2.5 text-sm font-black uppercase tracking-widest transition ${
                    highlight
                      ? "bg-amber-400 text-amber-950 hover:bg-amber-300 shadow-[0_2px_0_rgba(0,0,0,0.15)]"
                      : "bg-blue-700 text-white hover:bg-blue-800"
                  }`}
                >
                  Upgrade <ArrowSquareOut size={12} weight="bold" />
                </button>
                <PaymentTrust />
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
          Payments are processed by our external partner. You'll be redirected in a new tab.
        </p>
      </div>
    </DashboardLayout>
  );
}
